/**
 * DIE SYSTEMLEISTE (`<meta name="theme-color">`) TRAEGT DIE GEMALTE RAHMENFARBE —
 * IM SELBEN BILD WIE DER KOPF.
 *
 * Zwei Luecken, beide am 2026-10-23 in Labs gemessen (Cosmic Night, tasks/contacts):
 *
 * 1. NACH DEM LADEN. `janewayViewport` liefert feste Werte (`#fcfcfc`/`#000000`), der
 *    Rahmen kommt aber schon mit dem Server-Markup. Bis `ColorThemeProvider` nach der
 *    Hydration nachzieht, stand die Leiste daneben: Rahmen ab 77 ms, Leiste ab 336 ms,
 *    mit vierfacher CPU-Drosselung ab 1103 ms. Der Arbeitsbereichswechsel ist ein harter
 *    Reload (`arbeitsbereich-wechseln.ts`), daher sah man es genau dort.
 *    → `systemleisteVorpaintSkript`, ausgegeben von `ThemeProvider` vor jeder Huelle.
 *
 * 2. WAEHREND EINES FARBUEBERGANGS. `applyThemeVars` las `getComputedStyle(huelle)`
 *    direkt nach dem Umschalten — unter `data-theme-fx="soft"` laeuft dort ein
 *    400-ms-Uebergang, und der berechnete Wert ist dessen STARTWERT, also die alte
 *    Farbe. Die Leiste bekam erst nach `setTheme` (+460 ms) den neuen Ton.
 *    → `gemalteFarbe` misst an einem flachen Klon ohne Uebergang, sobald am Knoten
 *    eine Animation laeuft. Dieselbe Falle wie bei `messePalette` (0.700.0).
 */

const DURCHSICHTIG = /^rgba\(0,\s*0,\s*0,\s*0\)$/;

/** `true`, wenn die Farbe wirklich deckt — `rgba(0,0,0,0)` ist keine Flaeche. */
export function istGemalt(farbe: string | null | undefined): farbe is string {
  return !!farbe && !DURCHSICHTIG.test(farbe);
}

/**
 * Die Hintergrundfarbe, die `el` ZEIGEN WIRD — nicht die, die ein laufender
 * Uebergang gerade zeichnet.
 *
 * Ohne laufende Animation ist das `getComputedStyle`. Laeuft eine, wird ein flacher
 * Klon (gleiche Attribute, gleiche Inline-Variablen, gleicher Elternknoten, also
 * dieselben Regeln) mit `transition: none !important` daneben gehaengt, gelesen und
 * sofort entfernt. Inline-`!important` schlaegt auch die `!important`-Regel unter
 * `data-theme-fx="soft"`.
 */
export function gemalteFarbe(el: Element): string {
  const jetzt = getComputedStyle(el).backgroundColor;
  const laeuft = typeof el.getAnimations === "function" && el.getAnimations().length > 0;
  if (!laeuft || !el.parentNode) return jetzt;
  const probe = el.cloneNode(false) as HTMLElement;
  probe.removeAttribute("id");
  probe.setAttribute("aria-hidden", "true");
  probe.style.setProperty("position", "absolute", "important");
  probe.style.setProperty("width", "0", "important");
  probe.style.setProperty("height", "0", "important");
  probe.style.setProperty("visibility", "hidden", "important");
  probe.style.setProperty("transition", "none", "important");
  probe.style.setProperty("animation", "none", "important");
  el.parentNode.insertBefore(probe, el);
  try {
    return getComputedStyle(probe).backgroundColor;
  } finally {
    probe.remove();
  }
}

/**
 * Laeuft VOR der Hydration (Inline-Skript in `ThemeProvider`): sobald die Huelle im
 * Dokument steht, bekommen ALLE `theme-color`-Zeilen ihre gemalte Flaeche. Ohne Huelle
 * (Anmeldung, oeffentliche Seiten) zum Ende des Parsens der Hintergrund von `<html>` —
 * dieselbe Wahl wie `applyThemeVars` (`vars.background`).
 *
 * Ein MutationObserver, weil `ThemeProvider` VOR `AppLayout` im Markup steht: beim
 * Ausfuehren gibt es die Huelle noch nicht. Sein Rueckruf laeuft am Ende der
 * Parser-Aufgabe, die sie einfuegt — vor dem naechsten Bild.
 *
 * GESCHRIEBEN WIRD IN EIGENE ZEILEN VOR DEN SERVER-ZEILEN, nie in diese. Die Server-
 * Zeilen gehoeren React: es gleicht hoistbare `<meta>` bei der Hydration ueber ihre
 * Attribute ab, findet eine geaenderte `content` nicht wieder und haengt zwei neue an
 * (in Labs gemessen, 2026-10-24). Der Browser nimmt je Medium die ERSTE passende Zeile
 * in Baumreihenfolge — die eigene. `applyThemeVars` schreibt danach alle.
 */
export function systemleisteVorpaintSkript(): string {
  return (
    `try{(function(){var d=document,R=/^rgba\\(0,\\s*0,\\s*0,\\s*0\\)$/;` +
    `function z(c){if(!c||R.test(c))return;var e=d.querySelectorAll("meta[data-jw-leiste]");` +
    `if(!e.length){var m=d.querySelectorAll('meta[name="theme-color"]');for(var i=0;i<m.length;i++){var k=m[i].cloneNode(false);k.setAttribute("data-jw-leiste","");m[i].parentNode.insertBefore(k,m[i])}e=d.querySelectorAll("meta[data-jw-leiste]")}` +
    `for(var j=0;j<e.length;j++)e[j].content=c}` +
    `function s(){var h=d.querySelector("[data-jw-flaeche]");if(!h)return false;z(getComputedStyle(h).backgroundColor);return true}` +
    `if(s())return;var o=new MutationObserver(function(){if(s())o.disconnect()});` +
    `o.observe(d.documentElement,{childList:true,subtree:true});` +
    `d.addEventListener("DOMContentLoaded",function(){o.disconnect();if(!s())z(getComputedStyle(d.documentElement).backgroundColor)})` +
    `})()}catch(e){}`
  );
}
